import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Printer } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  PRODUCTION_CENTER_TYPES,
  PRODUCTION_JOB_LINE_STATUSES,
  PRODUCTION_JOB_STATUSES,
  useApi,
  type LazyArtworkDTO,
  type ProductDTO,
  type ProductionJobDTO,
  type ProductionJobLineDTO,
} from "../../api";
import { CENTER_TYPE_VARIANT, JOB_LINE_STATUS_VARIANT, JOB_STATUS_VARIANT } from "../../components/badges";
import { formatDate, objectUrl, Thumbnail, TwoLine } from "../../components/cells";

/** A job row with its lines nested underneath, the shape the tree renders. */
export interface JobNode {
  kind: "JOB";
  key: string;
  job: ProductionJobDTO;
  children: LineNode[];
}

export interface LineNode {
  kind: "LINE";
  key: string;
  job: ProductionJobDTO;
  line: ProductionJobLineDTO;
  children?: undefined;
}

export type TreeRow = JobNode | LineNode;

/**
 * The production queue as a two-level tree: one row per job, one child row per
 * line.
 *
 * A job is a unit of dispatch and a line is a unit of printing, and the
 * operator needs both — which job card to print, and which individual image is
 * still waiting. Flattening either away loses the thing the screen is for.
 */
export function ProductionJobTree({
  orderHeaderId,
  selectable = false,
  onSelectionChange,
  onPrintJobCard,
  toolbarLead,
}: {
  /** Scope to one order, or omit for the whole queue. */
  orderHeaderId?: number;
  selectable?: boolean;
  onSelectionChange?: (rows: TreeRow[]) => void;
  onPrintJobCard?: (jobId: number) => void;
  /** Sits before the grid's search — the queue switch on the jobs page. */
  toolbarLead?: ReactNode;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const jobs = useQuery({
    queryKey: ["production", "job", orderHeaderId ?? "all"],
    queryFn: () =>
      orderHeaderId === undefined
        ? api.productionJobs.findAll()
        : api.productionJobs.getByOrderHeaderId(orderHeaderId),
  });

  const artworkIds = useMemo(
    () =>
      Array.from(
        new Set(
          (jobs.data ?? []).flatMap((job) =>
            (job.lines ?? []).map((line) => line.artwork_id).filter((id): id is number => Boolean(id)),
          ),
        ),
      ),
    [jobs.data],
  );

  const productIds = useMemo(
    () =>
      Array.from(
        new Set(
          (jobs.data ?? []).flatMap((job) =>
            (job.lines ?? []).map((line) => line.product_id).filter((id): id is number => Boolean(id)),
          ),
        ),
      ),
    [jobs.data],
  );

  const artworks = useQuery({
    queryKey: ["artwork", "lazy", "by-ids", artworkIds],
    queryFn: () => api.lazyArtworks.findByIds(artworkIds),
    enabled: artworkIds.length > 0,
  });

  const products = useQuery({
    queryKey: ["product", "by-ids", productIds],
    queryFn: () => api.products.findByIds(productIds),
    enabled: productIds.length > 0,
  });

  const artworkById = useMemo(
    () => new Map((artworks.data ?? []).map((entry: LazyArtworkDTO) => [entry.id, entry])),
    [artworks.data],
  );
  const productById = useMemo(
    () => new Map((products.data ?? []).map((entry: ProductDTO) => [entry.id, entry])),
    [products.data],
  );

  const rows = useMemo<TreeRow[]>(
    () =>
      (jobs.data ?? []).map((job) => ({
        kind: "JOB" as const,
        key: `job-${job.id}`,
        job,
        children: (job.lines ?? []).map((line) => ({
          kind: "LINE" as const,
          key: `line-${line.id}`,
          job,
          line,
        })),
      })),
    [jobs.data],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["production", "job"] });

  const markLine = async (
    line: ProductionJobLineDTO,
    field: "printed_at" | "shipped_at",
    status: "PRINTED" | "SHIPPED",
    titleKey: string,
    titleFallback: string,
    textKey: string,
    textFallback: string,
  ) => {
    const ok = await confirm({
      title: t(titleKey, titleFallback),
      message: t(textKey, textFallback),
    });
    if (!ok) return;
    setBusy(true);
    try {
      // SUBMITTED is a terminal state on an external centre — stamping the
      // date is right but downgrading the status would lose that fact.
      const keepStatus =
        line.status === "SUBMITTED" || (status === "PRINTED" && line.status === "SHIPPED");
      await api.productionJobLines.update({
        ...line,
        [field]: new Date().toISOString(),
        status: keepStatus ? line.status : status,
      });
      toast.success(
        status === "PRINTED"
          ? t("notification.printed.success", "Job marked as printed successfully")
          : t("notification.shipped.success", "Job marked as shipped successfully"),
      );
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<TreeRow>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          if (row.kind === "JOB") return <Badge variant="info">{`Job #${row.job.id}`}</Badge>;
          const artwork = row.line.artwork_id ? artworkById.get(row.line.artwork_id) : undefined;
          return <Thumbnail url={objectUrl(artwork?.thumbnail?.image)} size="md" />;
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 120 },
      ),
      c.text(
        "order",
        (row) => (row.kind === "JOB" ? row.job.order?.order_no : row.job.order?.order_no) ?? "",
        { headerKey: "column.order", priority: 1 },
      ),
      c.custom(
        "status",
        (row) => (row.kind === "JOB" ? row.job.status : row.line.status),
        (row) => {
          if (row.kind === "JOB") {
            return <Badge variant={JOB_STATUS_VARIANT[row.job.status]}>{row.job.status}</Badge>;
          }
          const badge = (
            <Badge variant={JOB_LINE_STATUS_VARIANT[row.line.status]}>{row.line.status}</Badge>
          );
          return row.line.error_message ? (
            <span className="flex flex-col gap-0.5">
              {badge}
              <span className="text-[11px] text-risk">{row.line.error_message}</span>
            </span>
          ) : (
            badge
          );
        },
        {
          headerKey: "column.status",
          filter: "select",
          filterOptions: [
            ...new Set([...PRODUCTION_JOB_STATUSES, ...PRODUCTION_JOB_LINE_STATUSES]),
          ].map((value) => ({ value, label: value })),
          priority: 1,
        },
      ),
      c.custom(
        "type",
        (row) => row.job.production_center?.type ?? "",
        (row) => {
          const type = row.job.production_center?.type;
          return type ? <Badge variant={CENTER_TYPE_VARIANT[type]}>{type}</Badge> : <span>—</span>;
        },
        {
          headerKey: "column.type",
          filter: "select",
          filterOptions: PRODUCTION_CENTER_TYPES.map((value) => ({ value, label: value })),
          priority: 3,
        },
      ),
      c.custom(
        "task",
        (row) => {
          if (row.kind === "JOB") return "";
          const artwork = row.line.artwork_id ? artworkById.get(row.line.artwork_id) : undefined;
          const product = row.line.product_id ? productById.get(row.line.product_id) : undefined;
          return `${artwork?.metadata.code ?? row.line.snap_artwork_code ?? ""} ${
            product?.code ?? row.line.snap_product_code ?? ""
          }`;
        },
        (row) => {
          if (row.kind === "JOB") return <span />;
          const artwork = row.line.artwork_id ? artworkById.get(row.line.artwork_id) : undefined;
          const product = row.line.product_id ? productById.get(row.line.product_id) : undefined;
          return (
            <TwoLine
              primary={artwork?.metadata.code ?? row.line.snap_artwork_code}
              secondary={product?.code ?? row.line.snap_product_code}
            />
          );
        },
        { headerKey: "column.task", priority: 1 },
      ),
      c.number(
        "quantity",
        (row) =>
          row.kind === "JOB"
            ? (row.job.lines ?? []).reduce((sum, line) => sum + line.quantity, 0)
            : row.line.quantity,
        { headerKey: "column.quantity", priority: 3 },
      ),
      c.custom(
        "hr_file",
        () => "",
        (row) =>
          row.kind === "LINE" && objectUrl(row.line.hr_image) ? (
            <a
              href={objectUrl(row.line.hr_image)!}
              download
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              {t("value.download", "Download")}
            </a>
          ) : (
            <span className="text-ink-faint">{row.kind === "LINE" ? "Not available" : ""}</span>
          ),
        { headerKey: "column.hr-file", filter: "none", enableSorting: false, priority: 5 },
      ),
      c.custom(
        "production_file",
        () => "",
        (row) =>
          row.kind === "LINE" && objectUrl(row.line.production_image) ? (
            <a
              href={objectUrl(row.line.production_image)!}
              download
              target="_blank"
              rel="noreferrer"
              className="text-accent hover:underline"
            >
              {t("value.download", "Download")}
            </a>
          ) : (
            <span className="text-ink-faint">{row.kind === "LINE" ? "Not available" : ""}</span>
          ),
        { headerKey: "column.production-file", filter: "none", enableSorting: false, priority: 4 },
      ),
      c.text(
        "created_at",
        (row) => formatDate(row.kind === "JOB" ? row.job.created_at : row.line.created_at),
        { headerKey: "column.created-at", priority: 4 },
      ),
      c.custom(
        "dates",
        () => "",
        (row) => {
          if (row.kind !== "LINE") return <span />;
          const line = row.line;
          const canPrint = line.status === "READY_TO_PRINT" || line.status === "FAILED";
          const canShip = line.status === "PRINTED";

          if (canPrint) {
            return (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() =>
                  void markLine(
                    line,
                    "printed_at",
                    "PRINTED",
                    "dialog.confirm.printed.title",
                    "Mark as printed",
                    "dialog.confirm.printed.text",
                    "Are you sure you want to mark this job as printed?",
                  )
                }
              >
                {t("button.mark-printed", "Mark as printed")}
              </Button>
            );
          }
          if (canShip) {
            return (
              <span className="flex flex-col items-start gap-0.5">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    void markLine(
                      line,
                      "shipped_at",
                      "SHIPPED",
                      "dialog.confirm.shipped.title",
                      "Mark as shipped",
                      "dialog.confirm.shipped.text",
                      "Are you sure you want to mark this job as shipped?",
                    )
                  }
                >
                  {t("button.mark-shipped", "Mark as shipped")}
                </Button>
                <span className="text-[11px] text-ink-muted">
                  {`Printed at: ${formatDate(line.printed_at)}`}
                </span>
              </span>
            );
          }
          return (
            <TwoLine
              primary={line.printed_at ? `Printed: ${formatDate(line.printed_at)}` : "not printed"}
              secondary={line.shipped_at ? `Shipped: ${formatDate(line.shipped_at)}` : "not shipped"}
            />
          );
        },
        { headerKey: "column.dates", filter: "none", enableSorting: false, priority: 3 },
      ),
      c.custom(
        "action",
        () => "",
        (row) =>
          row.kind === "JOB" && onPrintJobCard ? (
            <Button
              size="sm"
              variant="primary"
              className="size-7 p-0"
              title={t("button.print", "Print")}
              icon={<Printer className="size-3.5" />}
              onClick={() => onPrintJobCard(row.job.id)}
            />
          ) : (
            <span />
          ),
        { headerKey: "column.action", filter: "none", enableSorting: false, priority: 99 },
      ),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworkById, busy, onPrintJobCard, productById, t]);

  return (
    <DataTable<TreeRow>
      data={rows}
      columns={cols}
      getRowId={(row) => row.key}
      loading={jobs.isLoading}
      getSubRows={(row) => (row.kind === "JOB" ? row.children : undefined)}
      defaultExpanded
      selectable={selectable}
      {...(onSelectionChange ? { onSelectionChange } : {})}
      toolbarLead={toolbarLead}
    />
  );
}
